import { useCallback, useEffect, useMemo, useRef, useTransition } from "react";

import { graphql, requestSubscription, useSubscription } from "react-relay";
import { getRequest } from "relay-runtime";

import { onSubscriptionGap } from "app/utils/subscriptionClient";

import type { useNodeEventsSubscription as DefaultSubscription } from "./__generated__/useNodeEventsSubscription.graphql";
import type {
  Disposable,
  GraphQLSubscriptionConfig,
  GraphQLTaggedNode,
  OperationType,
} from "relay-runtime";

/**
 * Any `nodeEvents` subscription: the hook's default, or a view's own.
 * `components` is there when the subscription selects it through the type it
 * watches, e.g. `... on BindingEvent { components }`.
 */
export interface NodeEventsOperation extends OperationType {
  readonly variables: { readonly nodeID: string };
  readonly response: {
    readonly nodeEvents: {
      readonly action: string;
      readonly nodeID: string;
      readonly components?: readonly string[];
    };
  };
}

type NodeEvent = NodeEventsOperation["response"]["nodeEvents"];

/** The component names a subscription's events can carry. */
type ComponentOf<TSubscription extends NodeEventsOperation> = NonNullable<
  TSubscription["response"]["nodeEvents"]["components"]
>[number];

/**
 * Whether a view asked for this event.
 *
 * `components` narrows UPDATED events only. An update must name at least one
 * of them: it lists every part that changed in one transaction, and a view
 * wants it if any is one it renders, so a change to the node's own fields
 * (which names none) is ignored. A node being created or removed changes
 * every part of it, so CREATED and REMOVED always count. Without
 * `components`, everything counts.
 */
function wanted(
  event: NodeEvent,
  components: readonly string[] | undefined,
): boolean {
  if (!components?.length || event.action !== "UPDATED") {
    return true;
  }
  return (event.components ?? []).some((component) =>
    components.includes(component),
  );
}

/**
 * Stands in for `requestSubscription` when there is nothing to watch.
 * `useSubscription` has no switch to turn it off, and a hook cannot be called
 * conditionally, so this is how a view with no id yet subscribes to nothing.
 */
const subscribeToNothing = (): Disposable => ({ dispose: () => {} });

/**
 * The least time between two refetches of one view.
 *
 * A busy node can report several times a second (a binding run's counts move
 * with every batch of executions saved), and every viewer refetches on each
 * event, so the API sees events times viewers. The first event in a quiet
 * spell refetches at once; any more inside this window become one refetch at
 * its end, so the view still ends up current.
 */
export const MIN_REFETCH_INTERVAL_MS = 2000;

type Options<TSubscription extends NodeEventsOperation, TRefetch> = {
  /** The view's refetch, from its refetchable fragment. */
  refetch: TRefetch;
  /**
   * Refetch once on starting to watch a node, for a view that can mount from
   * data its page fetched earlier. Events only reach a mounted view, so
   * anything that changed in between (a scan that ran while another tab or
   * page was showing) would never be shown.
   */
  catchUpOnSubscribe?: boolean;
} & (
  | {
      /** React to every event, including a change to the node itself. */
      subscription?: undefined;
      components?: undefined;
    }
  | {
      /**
       * The view's own `nodeEvents` subscription, selecting `components`
       * through the type it watches:
       *
       *     subscription: graphql`
       *       subscription RunsNodeEventsSubscription($nodeID: ID!) {
       *         nodeEvents(nodeID: $nodeID) {
       *           action
       *           nodeID
       *           ... on BindingEvent { components }
       *         }
       *       }
       *     `,
       *
       * Written inline in the view's own call, like any other operation it
       * depends on. Views whose documents select the same fields for the same
       * node share one subscription on the wire (see `subscriptionClient`).
       */
      subscription: GraphQLTaggedNode;
      /**
       * Only react to updates naming at least one of these parts, checked
       * against that type's component enum (e.g. `RUNS` on a binding).
       */
      components: readonly ComponentOf<TSubscription>[];
    }
);

/**
 * Refetch a view when the node it shows changes: `useSubscription` on
 * `nodeEvents`, plus the three things every such view needs from it.
 *
 * - Nothing to watch yet (a listing's set id still loading) subscribes to
 *   nothing, where `useSubscription` alone would need a conditional call.
 * - Events are thin, "this node changed, in these parts", so the response is
 *   always a refetch through the view's own query, which keeps authorization
 *   and field selection where they are. The changed node's record is also
 *   invalidated, so a view showing it that isn't mounted refetches on its
 *   next mount rather than reading a stale store.
 * - The config is memoised on the id and components, since `useSubscription`
 *   resubscribes whenever its config changes identity.
 * - Refetches are throttled to one per `MIN_REFETCH_INTERVAL_MS`, with a
 *   trailing one, so a busy node can't make every viewer hammer the API.
 * - A view that can mount from data fetched before it subscribed, such as a
 *   tab rendering its page's query, passes `catchUpOnSubscribe` to refetch
 *   once as it starts watching.
 *
 * `nodeID` is a node's own id to watch that node, or a set's id
 * (`AccountGroupSet` and siblings) to watch every node of a type.
 *
 * A view that only wants some parts of a node passes its own subscription and
 * names them, typing the hook with that subscription so the names are checked:
 * `useNodeEvents<RunsNodeEventsSubscription>(id, { subscription, components:
 * ["RUNS"], refetch })`.
 */
export function useNodeEvents<
  TSubscription extends NodeEventsOperation = DefaultSubscription,
  TRefetch extends (
    variables: object,
    options: { fetchPolicy: "store-and-network" },
  ) => unknown = (
    variables: object,
    options: { fetchPolicy: "store-and-network" },
  ) => unknown,
>(
  nodeID: string | null | undefined,
  {
    subscription,
    components,
    refetch,
    catchUpOnSubscribe = false,
  }: Options<TSubscription, TRefetch>,
): void {
  const [, startTransition] = useTransition();

  // Through a ref so a fresh refetch identity each render doesn't resubscribe.
  // Updated in an effect rather than during render: it is only read from
  // `onNext`, and this effect runs before the one `useSubscription` owns.
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });

  // When the view last refetched, and the trailing refetch already scheduled,
  // if any (see `MIN_REFETCH_INTERVAL_MS`).
  const lastRefetchRef = useRef(Number.NEGATIVE_INFINITY);
  const trailingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A trailing refetch belongs to the subscription that asked for it: drop it
  // when the view unmounts or starts watching another node.
  useEffect(
    () => () => {
      if (trailingRef.current !== null) {
        clearTimeout(trailingRef.current);
        trailingRef.current = null;
      }
    },
    [nodeID],
  );

  // Refetch now, or once the throttle's window ends if the view refetched
  // too recently (see `MIN_REFETCH_INTERVAL_MS`). Only reads refs, so the
  // same function serves events and gaps.
  const refetchSoon = useCallback(() => {
    const refetchNow = () => {
      lastRefetchRef.current = Date.now();
      startTransition(() => {
        refetchRef.current({}, { fetchPolicy: "store-and-network" });
      });
    };
    const wait = lastRefetchRef.current + MIN_REFETCH_INTERVAL_MS - Date.now();
    if (wait <= 0) {
      refetchNow();
    } else if (trailingRef.current === null) {
      trailingRef.current = setTimeout(() => {
        trailingRef.current = null;
        refetchNow();
      }, wait);
    }
  }, []);

  // A fresh `["RUNS"]` literal each render must not resubscribe either.
  const componentsKey = components?.join("\n") ?? "";

  const config = useMemo((): GraphQLSubscriptionConfig<NodeEventsOperation> => {
    const parts = componentsKey ? componentsKey.split("\n") : undefined;
    return {
      // The default selects only what every `NodeEvent` has, for a view that
      // reacts to any change. Events are thin, "this node changed", so the
      // view refetches through its own query, which keeps authorization and
      // field selection where they already are.
      //
      // `nodeID` is always the node that changed, even when watching a set,
      // so it matches the id Relay already stores for that record. It isn't
      // `id`, so Relay doesn't store the event over that record.
      subscription:
        subscription ??
        graphql`
          subscription useNodeEventsSubscription($nodeID: ID!) {
            nodeEvents(nodeID: $nodeID) {
              action
              nodeID
            }
          }
        `,
      variables: { nodeID: nodeID ?? "" },
      updater: (store, data) => {
        const event = data?.nodeEvents;
        if (event && wanted(event, parts)) {
          store.get(event.nodeID)?.invalidateRecord();
        }
      },
      onNext: (response) => {
        const event = response?.nodeEvents;
        if (event && wanted(event, parts)) {
          refetchSoon();
        }
      },
      onError: (error) => {
        // Refused by the server; the transport doesn't retry a refusal.
        console.warn("nodeEvents subscription refused", error);
      },
    };
  }, [nodeID, componentsKey, subscription, refetchSoon]);

  // The transport keeps the subscription alive, but anything sent while it
  // was reconnecting or resubscribing is lost: catch up when it says so,
  // for a reconnect of the whole socket or a resubscribe of this operation.
  useEffect(() => {
    if (!nodeID) {
      return;
    }
    return onSubscriptionGap(({ operation }) => {
      if (
        !operation ||
        (operation.variables.nodeID === nodeID &&
          operation.name === getRequest(config.subscription).params.name)
      ) {
        refetchSoon();
      }
    });
  }, [nodeID, config, refetchSoon]);

  useSubscription(config, nodeID ? requestSubscription : subscribeToNothing);

  // Nothing sent before the view subscribed reaches it, so a view that may be
  // showing older data catches up as it starts watching. After
  // `useSubscription`, whose effect subscribes, so the subscribe is sent
  // before the query rather than leaving a window between them. Every run
  // catches up, StrictMode's second one included: an `<Activity>` shown again
  // reruns effects the same way, with its subscription just as new.
  useEffect(() => {
    if (nodeID && catchUpOnSubscribe) {
      refetchSoon();
    }
  }, [nodeID, catchUpOnSubscribe, refetchSoon]);
}
